import * as Sentry from "@sentry/react";

/**
 * Detect if the browser is Safari (needed to disable features that use localStorage)
 */
const isSafariBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
};

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Call this function once at app startup before rendering React
 * Wrapped in try-catch to prevent crashes in Safari Private Mode
 */
export const initSentry = () => {
  try {
    // Only initialize in production or when explicitly enabled
    const isProduction = import.meta.env.PROD;
    const isDevelopment = import.meta.env.DEV;

    // Skip initialization in development unless explicitly enabled
    if (isDevelopment) {
      console.log("Sentry: Skipping initialization in development mode");
      return;
    }

    // Build integrations based on browser type
    // Only add replay integration for non-Safari browsers
    // replayIntegration uses localStorage which can crash in Safari Private Mode
    const isSafari = isSafariBrowser();
    
    if (isSafari) {
      console.log("Sentry: Disabling replay integration for Safari compatibility");
    }

    Sentry.init({
      dsn: "https://12d2b0657ba4aea6f2cc34d1cc1e815b@o4510206539792384.ingest.us.sentry.io/4510213012062208",
      
      // Integrations - conditionally include replay for non-Safari
      integrations: isSafari 
        ? [Sentry.browserTracingIntegration()]
        : [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
              maskAllInputs: true,
            }),
          ],
      // Performance Monitoring
      tracesSampleRate: 0.1,
      
      // Session Replay Sampling (only applies when replay is enabled)
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      // Environment
      environment: isProduction ? "production" : "development",

      // Filter out localhost errors (don't send to Sentry)
      beforeSend(event, hint) {
        // Filter out known third-party in-app browser noise we cannot fix
        // (e.g. Facebook iOS in-app browser injects a script that references
        // window.webkit.messageHandlers without guarding for its existence)
        const message =
          hint?.originalException instanceof Error
            ? hint.originalException.message
            : (event.exception?.values?.[0]?.value ?? event.message ?? '');

        if (
          typeof message === 'string' &&
          message.includes("'window.webkit.messageHandlers'")
        ) {
          return null;
        }

        // Don't send errors from localhost
        if (typeof window !== 'undefined' && 
            (window.location.hostname === 'localhost' || 
             window.location.hostname === '127.0.0.1')) {
          console.log("Sentry: Filtering out localhost error", event);
          return null;
        }
        return event;
      },

      // Add release information (optional, can be set during build)
      // release: "godyspo@1.0.0",
    });

    console.log("Sentry initialized successfully");
  } catch (error) {
    // Sentry initialization failed - app continues to work without error tracking
    console.warn("Sentry: Initialization failed, continuing without error tracking:", error);
  }
};

/**
 * Set user context in Sentry for better error tracking
 * Call this after user login
 */
export const setSentryUser = (user: { id: string; email?: string }, role?: 'buyer' | 'seller') => {
  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      role: role,
    });
    console.log("Sentry user context set:", { id: user.id, role });
  } catch (error) {
    console.error("Failed to set Sentry user context:", error);
  }
};

/**
 * Clear user context in Sentry
 * Call this on user logout
 */
export const clearSentryUser = () => {
  try {
    Sentry.setUser(null);
    console.log("Sentry user context cleared");
  } catch (error) {
    console.error("Failed to clear Sentry user context:", error);
  }
};

/**
 * Manually capture an exception
 * Use this for try-catch blocks where you want to report errors to Sentry
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  try {
    if (context) {
      Sentry.setContext("additional_context", context);
    }
    Sentry.captureException(error);
  } catch (e) {
    console.error("Failed to capture exception in Sentry:", e);
  }
};

export default Sentry;
